/**
 * Real voice through LiveKit (T6, docs/ARCHITECTURE.md §9). Selected by `useVoice.ts` only when the server
 * issues `provider: 'livekit'` credentials, so the 2D/simulated-only paths — and every session when no live
 * provider is configured — never pull `livekit-client` into the bundle. `livekit-client` is dynamically
 * imported inside `connect()`, mirroring how `packages/client/src/renderers/board3d.tsx` lazy-loads `three`.
 *
 * The microphone is never enabled in `connect()`: publishing only ever starts from `setMuted(false)`, which
 * must run on a user gesture (a click), per the in-person/remote policy in `useVoice.ts`. `getUserMedia`
 * failures are classified into the two client-facing statuses the panel explains: `permission_denied` (the
 * browser/OS declined) and `device_error` (no working microphone, or one already in use).
 *
 * EXTERNALLY BLOCKED: no LiveKit deployment or credentials exist in this environment (see
 * packages/server/src/voice/livekit.ts's own note), so this path is exercised here only up to typechecking —
 * never against a real LiveKit server or a real microphone prompt.
 */
import type * as LiveKitNS from 'livekit-client';
import type { VoiceClient, VoiceConnectionStatus, VoiceCredentials } from '@momentum/shared';
import { attachRemoteAudio, detachRemoteAudio } from './remoteAudio.ts';

export interface LiveKitVoiceClient extends VoiceClient {
  readonly provider: 'livekit';
}

function errorDetail(err: unknown): string {
  return err instanceof Error ? err.message : 'Could not access the microphone';
}

function isPermissionDenied(lk: typeof LiveKitNS, err: unknown): boolean {
  if (err instanceof DOMException && (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError')) return true;
  return lk.MediaDeviceFailure.getFailure(err) === lk.MediaDeviceFailure.PermissionDenied;
}

export type LiveKitLoader = () => Promise<typeof LiveKitNS>;

export function createLiveKitVoiceClient(loadLiveKit: LiveKitLoader = () => import('livekit-client')): LiveKitVoiceClient {
  let room: LiveKitNS.Room | null = null;
  let status: VoiceConnectionStatus = 'idle';
  let muted = true;
  let connectSequence = 0;
  let audioPlaybackBlocked = false;
  let cleanupListeners: Array<() => void> = [];
  const attachedAudioTracks = new Set<LiveKitNS.RemoteAudioTrack>();

  const statusListeners = new Set<(status: VoiceConnectionStatus, detail?: string) => void>();
  const speakingListeners = new Set<(identities: string[]) => void>();

  function setStatus(next: VoiceConnectionStatus, detail?: string): void {
    status = next;
    for (const listener of statusListeners) listener(status, detail);
  }

  function detachRoom(target = room): void {
    if (room !== target) return;
    for (const cleanup of cleanupListeners) cleanup();
    cleanupListeners = [];
    for (const track of attachedAudioTracks) detachRemoteAudio(track);
    attachedAudioTracks.clear();
    for (const listener of speakingListeners) listener([]);
    if (room === target) room = null;
  }

  function reportAudioPlayback(target: LiveKitNS.Room): void {
    audioPlaybackBlocked = !target.canPlaybackAudio;
    setStatus('connected', audioPlaybackBlocked ? 'Your browser paused huddle audio. Tap Enable huddle audio to listen.' : undefined);
  }

  return {
    provider: 'livekit',

    async connect(credentials: VoiceCredentials): Promise<void> {
      if (credentials.provider !== 'livekit') {
        setStatus('failed', 'LiveKit client received non-livekit credentials');
        return;
      }

      const sequence = ++connectSequence;
      const previous = room;
      detachRoom(previous);
      if (previous) {
        try { await previous.disconnect(); } catch { /* The prior room is already closed. */ }
      }
      if (sequence !== connectSequence) return;
      muted = true;
      audioPlaybackBlocked = false;
      setStatus('connecting');

      // Everything below can fail in ways outside our control (SDK load, WebRTC support, the network call).
      // None of it may ever throw out of `connect()` — gameplay never depends on voice (T6 §3) — so the whole
      // attempt is one try/catch that always resolves to a status instead.
      try {
        const lk = await loadLiveKit();
        if (sequence !== connectSequence) return;
        const nextRoom = new lk.Room({ adaptiveStream: false, dynacast: false });
        const current = () => sequence === connectSequence && room === nextRoom;
        const onReconnecting = () => { if (current()) setStatus('reconnecting'); };
        const onReconnected = () => { if (current()) reportAudioPlayback(nextRoom); };
        const onDisconnected = () => { if (current()) { detachRoom(nextRoom); setStatus('disconnected'); } };
        const onActiveSpeakersChanged = (speakers: LiveKitNS.Participant[]) => {
          if (!current()) return;
          const identities = speakers.map((p) => p.identity);
          for (const listener of speakingListeners) listener(identities);
        };
        const onTrackSubscribed = (track: LiveKitNS.RemoteTrack) => {
          if (!current() || track.kind !== lk.Track.Kind.Audio) return;
          const audioTrack = track as LiveKitNS.RemoteAudioTrack;
          attachRemoteAudio(audioTrack);
          attachedAudioTracks.add(audioTrack);
        };
        const onTrackUnsubscribed = (track: LiveKitNS.RemoteTrack) => {
          if (track.kind !== lk.Track.Kind.Audio) return;
          const audioTrack = track as LiveKitNS.RemoteAudioTrack;
          detachRemoteAudio(audioTrack);
          attachedAudioTracks.delete(audioTrack);
        };
        const onAudioPlaybackStatusChanged = () => { if (current()) reportAudioPlayback(nextRoom); };
        nextRoom.on(lk.RoomEvent.Reconnecting, onReconnecting);
        nextRoom.on(lk.RoomEvent.Reconnected, onReconnected);
        nextRoom.on(lk.RoomEvent.Disconnected, onDisconnected);
        nextRoom.on(lk.RoomEvent.ActiveSpeakersChanged, onActiveSpeakersChanged);
        nextRoom.on(lk.RoomEvent.TrackSubscribed, onTrackSubscribed);
        nextRoom.on(lk.RoomEvent.TrackUnsubscribed, onTrackUnsubscribed);
        nextRoom.on(lk.RoomEvent.AudioPlaybackStatusChanged, onAudioPlaybackStatusChanged);
        const listenerCleanup = [
          () => nextRoom.off(lk.RoomEvent.Reconnecting, onReconnecting),
          () => nextRoom.off(lk.RoomEvent.Reconnected, onReconnected),
          () => nextRoom.off(lk.RoomEvent.Disconnected, onDisconnected),
          () => nextRoom.off(lk.RoomEvent.ActiveSpeakersChanged, onActiveSpeakersChanged),
          () => nextRoom.off(lk.RoomEvent.TrackSubscribed, onTrackSubscribed),
          () => nextRoom.off(lk.RoomEvent.TrackUnsubscribed, onTrackUnsubscribed),
          () => nextRoom.off(lk.RoomEvent.AudioPlaybackStatusChanged, onAudioPlaybackStatusChanged),
        ];
        cleanupListeners = listenerCleanup;
        room = nextRoom;

        // Subscribe-only is the default for every viewer (spectators included): joining a room never implies
        // publishing. Only `setMuted(false)` — a user gesture — ever turns the microphone on.
        await nextRoom.connect(credentials.url, credentials.token);
        if (!current()) {
          for (const cleanup of listenerCleanup) cleanup();
          try { await nextRoom.disconnect(); } catch { /* Superseded during connection. */ }
          return;
        }
        reportAudioPlayback(nextRoom);
      } catch (err) {
        if (sequence !== connectSequence) return;
        const failedRoom = room;
        detachRoom(failedRoom);
        if (failedRoom) try { await failedRoom.disconnect(); } catch { /* Failed connection is already closed. */ }
        setStatus('failed', errorDetail(err));
      }
    },

    async disconnect(): Promise<void> {
      connectSequence += 1;
      const current = room;
      detachRoom();
      audioPlaybackBlocked = false;
      if (current) {
        try {
          await current.disconnect();
        } catch {
          // Already gone; nothing left to clean up.
        }
      }
      setStatus('disconnected');
    },

    async startAudio(): Promise<boolean> {
      const current = room;
      if (!current) return false;
      try {
        await current.startAudio();
        if (room !== current) return false;
        reportAudioPlayback(current);
        return !audioPlaybackBlocked;
      } catch (err) {
        if (room !== current) return false;
        audioPlaybackBlocked = true;
        setStatus('connected', `Huddle audio is still paused: ${errorDetail(err)}`);
        return false;
      }
    },

    isAudioPlaybackBlocked(): boolean {
      return audioPlaybackBlocked;
    },

    async setMuted(next: boolean): Promise<void> {
      const current = room;
      if (!current) {
        muted = next;
        return;
      }
      if (next) {
        try {
          await current.localParticipant.setMicrophoneEnabled(false);
        } catch {
          // Turning the mic off failing is not user-actionable; the muted intent still holds.
        }
        if (room !== current) return;
        muted = true;
        return;
      }
      try {
        // This call is inside the microphone button's click handler and also unlocks incoming audio.
        try {
          await current.startAudio();
        } catch {
          if (room !== current) return;
          audioPlaybackBlocked = true;
        }
        if (room !== current) return;
        await current.localParticipant.setMicrophoneEnabled(true);
        if (room !== current) {
          try { await current.localParticipant.setMicrophoneEnabled(false); } catch { /* Old room is closing. */ }
          return;
        }
        muted = false;
        reportAudioPlayback(current);
      } catch (err) {
        if (room !== current) return;
        muted = true;
        try {
          const lk = await loadLiveKit();
          setStatus(isPermissionDenied(lk, err) ? 'permission_denied' : 'device_error', errorDetail(err));
        } catch {
          setStatus('device_error', errorDetail(err));
        }
      }
    },

    isMuted(): boolean {
      return muted;
    },

    status(): VoiceConnectionStatus {
      return status;
    },

    onSpeaking(listener) {
      speakingListeners.add(listener);
      return () => speakingListeners.delete(listener);
    },

    onStatus(listener) {
      statusListeners.add(listener);
      return () => statusListeners.delete(listener);
    },
  };
}
